import { Link } from "wouter";
import {
  Rocket, TrendingUp, Award, FileText, ArrowRight, MessageSquare, Zap,
  Handshake, CircleDot, Users2, Calendar
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { RoleBasedNextSteps, OnboardingProgress } from "@/components/whatsnext";
import { LazySection } from "@/components/LazySection";
import { AIRecommendations } from "@/components/AIRecommendations";
import { ActivityFeed } from "@/components/ActivityFeed";
import type { User as UserType, Article, Event } from "@shared/schema";

interface OverviewTabProps {
  user: UserType;
  dashboardStats: any;
  articles: Article[];
  events: Event[];
  leaders: UserType[];
  connections: any[];
  statsLoading: boolean;
  articlesLoading: boolean;
  eventsLoading: boolean;
  leadersLoading: boolean;
  connectionsLoading: boolean;
}

export function OverviewTab({ user, dashboardStats }: OverviewTabProps) {
  const approvedRoles = dashboardStats?.approvedRoles || [];

  return (
    <div className="space-y-8">
      <section className="grid gap-6 lg:grid-cols-2" data-testid="section-whats-next">
        <RoleBasedNextSteps user={user} approvedRoles={approvedRoles} />
        <OnboardingProgress user={user} />
      </section>

      {approvedRoles.length > 0 && (
        <section data-testid="section-overview-roles">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Your Workspace</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {approvedRoles.includes("founder") && (
              <Link href="/home?tab=founder">
                <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-founder">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-500/10 dark:bg-green-400/10 flex items-center justify-center shrink-0">
                        <Rocket className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-foreground">Founder Hub</h4>
                        <p className="text-sm text-muted-foreground truncate">
                          {user.founderCompanyName || "Capital, directory & pitch tools"}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
            {approvedRoles.includes("investor") && (
              <Link href="/home?tab=investor">
                <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-investor">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-blue-500/10 dark:bg-blue-400/10 flex items-center justify-center shrink-0">
                        <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-foreground">Investor Hub</h4>
                        <p className="text-sm text-muted-foreground">
                          {(dashboardStats?.pendingIntros ?? 0) > 0 ? `${dashboardStats.pendingIntros} pending intros` : "Deal flow & portfolio"}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
            {approvedRoles.includes("executive") && (
              <Link href="/home?tab=executive">
                <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-executive">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-500/10 dark:bg-purple-400/10 flex items-center justify-center shrink-0">
                        <Award className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-foreground">Executive Hub</h4>
                        <p className="text-sm text-muted-foreground">Board & leadership</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
            {approvedRoles.includes("contributor") && (
              <Link href="/home?tab=contributor">
                <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-contributor">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-orange-500/10 dark:bg-orange-400/10 flex items-center justify-center shrink-0">
                        <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-foreground">Contributor Hub</h4>
                        <p className="text-sm text-muted-foreground">Content & publishing</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
            {approvedRoles.includes("advisor") && (
              <Link href="/home?tab=advisor">
                <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-advisor">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-teal-500/10 dark:bg-teal-400/10 flex items-center justify-center shrink-0">
                        <Handshake className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-foreground">Advisor Hub</h4>
                        <p className="text-sm text-muted-foreground">Mentorship & advisory</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            <Link href="/messages">
              <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-messages">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-foreground">Messages</h4>
                      <p className="text-sm text-muted-foreground">
                        {(dashboardStats?.unreadMessages ?? 0) > 0 ? `${dashboardStats.unreadMessages} unread` : "All caught up"}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            {dashboardStats && (dashboardStats.creditBalance > 0 || dashboardStats.monthlyCredits > 0) && (
              <Link href="/account#billing">
                <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-credits">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                        <Zap className="h-5 w-5 text-secondary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-foreground">Credits</h4>
                        <p className="text-sm text-muted-foreground">
                          {dashboardStats.creditBalance ?? 0} available
                          {dashboardStats.monthlyCredits > 0 && ` (${dashboardStats.monthlyCredits}/mo)`}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>
        </section>
      )}

      <section data-testid="section-overview-community">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Community</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link href="/circles">
            <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-circles">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-indigo-500/10 dark:bg-indigo-400/10 flex items-center justify-center shrink-0">
                    <CircleDot className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-foreground">Peer Circles</h4>
                    <p className="text-sm text-muted-foreground">Mentorship & peer support groups</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link href="/investor-network">
            <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-clubs">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-500/10 dark:bg-amber-400/10 flex items-center justify-center shrink-0">
                    <Users2 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-foreground">Clubs</h4>
                    <p className="text-sm text-muted-foreground">Investment & special interest clubs</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link href="/network/office-hours">
            <Card className="hover-elevate cursor-pointer h-full" data-testid="card-overview-office-hours">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-rose-500/10 dark:bg-rose-400/10 flex items-center justify-center shrink-0">
                    <Calendar className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-foreground">Office Hours</h4>
                    <p className="text-sm text-muted-foreground">Book or host mentorship sessions</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      <LazySection
        className=""
        minHeight="200px"
        fallback={
          <div className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-40 w-full rounded-md" />
              ))}
            </div>
          </div>
        }
      >
        <AIRecommendations />
      </LazySection>

      <LazySection
        className=""
        minHeight="300px"
        fallback={
          <div className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        }
      >
        <section data-testid="section-overview-activity">
          <ActivityFeed />
        </section>
      </LazySection>
    </div>
  );
}
